"use client";

import React, { useEffect, useState } from "react";
import { stegaClean } from "@sanity/client/stega";
import { cn } from "@/lib/utils";
import { owners } from "@/helpers/owners";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@/shadcn/ui/dialog";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { HomePopupFrequency, PageHomeNewSchema } from "@/sanity/pages/PageHomeNewTemplate";
import { COUNTDOWN_UNITS, Highlight, useCountdown } from "@/components/trademania-2/shared";

import closeIcon from "@/public/trademania-2/popup-close.svg";

const STORAGE_KEY = "tm2EventPopup";
const DEFAULT_DELAY = 0;

/** Whether this visitor already saw the popup within the frequency window. Storage can throw in private mode. */
function wasShown(frequency: HomePopupFrequency) {
  try {
    if (frequency === "session") return sessionStorage.getItem(STORAGE_KEY) !== null;
    if (frequency === "day") return localStorage.getItem(STORAGE_KEY) === new Date().toDateString();
  } catch {}
  return false;
}

function markShown(frequency: HomePopupFrequency) {
  try {
    if (frequency === "session") sessionStorage.setItem(STORAGE_KEY, "1");
    if (frequency === "day") localStorage.setItem(STORAGE_KEY, new Date().toDateString());
  } catch {}
}

function PopupCountdown({ target }: { target?: string }) {
  const parts = useCountdown(target);
  return (
    <div className="flex gap-[1.25rem]">
      {COUNTDOWN_UNITS.map(([key, unit]) => (
        <div key={key} className="flex min-w-0 flex-1 flex-col items-center gap-[1rem]">
          <span className="flex h-[8rem] w-full items-center justify-center rounded-[6px] border border-white/[.06] bg-white/[.09] font-bebas text-[6rem] leading-none text-white">
            {parts[key]}
          </span>
          <span className="font-geist text-[1.625rem] font-medium leading-none tracking-[.06em] text-white/50">
            {unit}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * TradeMania 2.0 launch popup on the homepage: opens after entering the page, video on top.
 * Portalled to <body>, so it carries the `tm2` margin reset and the Owners font variable itself.
 */
export default function EventPopup({ data }: { data: PageHomeNewSchema }) {
  const [open, setOpen] = useState(false);
  const enabled = data?.popupEnabled === true;
  const frequency = (stegaClean(data?.popupFrequency) ?? "day") as HomePopupFrequency;
  const delay = Math.max(0, data?.popupDelay ?? DEFAULT_DELAY);

  useEffect(() => {
    if (!enabled || wasShown(frequency)) return;
    const id = window.setTimeout(() => {
      markShown(frequency);
      setOpen(true);
    }, delay * 1000);
    return () => window.clearTimeout(id);
  }, [enabled, frequency, delay]);

  if (!enabled) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        // no subtitle: tell Radix the missing description is intended
        {...(!data?.popupSubtitle && { "aria-describedby": undefined })}
        className={cn(
          // inset ring = Figma's inside stroke, so it takes no room from the 9px / 36px insets
          "tm2 max-h-[calc(100svh-2rem)] w-[49.75rem] max-w-[calc(100%-2rem)] overflow-y-auto rounded-[1rem] bg-[#10110E] p-0 pt-[1rem] font-geist text-white ring-1 ring-inset ring-white/[.08] lg:w-[80rem] lg:p-0 lg:pt-[1rem] [&>.close-btn]:hidden",
          owners.variable,
        )}>
        <div className="relative mx-[1.125rem] h-[27.875rem] overflow-clip rounded-[.5rem] bg-black">
          {data?.popupVideo?.url && (
            <video
              className="absolute inset-0 h-full w-full object-cover"
              src={data.popupVideo.url}
              poster={data?.popupPoster?.src}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            />
          )}
          <div className="absolute inset-x-[1.5rem] top-[1.5rem] flex items-center gap-[1rem]">
            {data?.popupChip && (
              <span className="whitespace-pre rounded-[.5rem] bg-[#10110E]/50 px-[1.5rem] py-[1.125rem] font-geist text-[1.75rem] font-medium leading-[2.125rem] tracking-[.02em] backdrop-blur-[14.05px]">
                <span aria-hidden>
                  {" "}
                  <span className="text-[#A3CC4B]/20">/</span>
                  <span className="text-[#A3CC4B]/50">/</span>
                  <span className="text-[#A3CC4B]">/</span>
                  {"  "}
                </span>
                <span className="text-[1.625rem] uppercase tracking-[.04em]">{data.popupChip}</span>
              </span>
            )}
            <DialogClose className="ml-auto flex shrink-0 items-center rounded-[.5rem] bg-[#10110E]/50 px-[1.25rem] py-[1.125rem] backdrop-blur-[14.05px] transition-colors hover:bg-[#10110E]/80">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={closeIcon.src} alt="" width={18} height={18} className="h-[2.25rem] w-[2.25rem]" />
              <span className="sr-only">Schließen</span>
            </DialogClose>
          </div>
        </div>

        <div className="flex flex-col gap-[3rem] p-[3rem] lg:px-[4.5rem] lg:pb-[4.5rem] lg:pt-[4rem]">
          <div className="flex flex-col gap-[1.5rem]">
            <DialogTitle className="font-owners text-[4.25rem] font-medium uppercase leading-none tracking-[.01em] text-white lg:text-[5rem]">
              {data?.popupTitle}{" "}
              {data?.popupTitleHighlight && (
                <Highlight theme="dark" className="text-[#D9F9B9] lg:italic">
                  {data.popupTitleHighlight}
                </Highlight>
              )}
            </DialogTitle>
            {data?.popupSubtitle && (
              <DialogDescription className="font-geist text-[2.25rem] font-medium leading-[1.35] text-white lg:text-[2.5rem]">
                {data.popupSubtitle}
              </DialogDescription>
            )}
          </div>

          {(data?.popupText || data?.popupTextEmphasis) && (
            <div className="flex flex-col gap-[1rem] font-geist text-[2rem] leading-[1.4] [text-wrap:pretty] lg:leading-[1.5]">
              {data?.popupText && <p className="text-white/70">{data.popupText}</p>}
              {data?.popupTextEmphasis && <p className="font-medium text-white">{data.popupTextEmphasis}</p>}
            </div>
          )}

          <PopupCountdown target={stegaClean(data?.popupCountdownDate)} />

          {data?.popupCTA && (
            <CTACornerButton
              data={data.popupCTA}
              size="block"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
